import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useLocation } from "wouter";
import CreatorBadge from "@/components/CreatorBadge";

interface FollowUser {
  id: string;
  username: string | null;
  displayName: string | null;
  profileImageUrl: string | null;
  isVerified: boolean;
  subscriptionTier?: string | null;
  accentColor?: string | null;
  bio: string | null;
}

interface FollowListModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  username: string;
  initialTab: "followers" | "following";
}

export default function FollowListModal({
  isOpen,
  onClose,
  userId,
  username,
  initialTab,
}: FollowListModalProps) {
  const [tab, setTab] = useState<"followers" | "following">(initialTab);
  const [, navigate] = useLocation();

  const { data: followers, isLoading: followersLoading } = useQuery<{
    users: FollowUser[];
    private?: boolean;
  }>({
    queryKey: [`/api/users/${userId}/followers`],
    enabled: isOpen,
  });

  const { data: following, isLoading: followingLoading } = useQuery<{
    users: FollowUser[];
    private?: boolean;
  }>({
    queryKey: [`/api/users/${userId}/following`],
    enabled: isOpen,
  });

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  if (!isOpen) return null;

  const currentData = tab === "followers" ? followers : following;
  const users = currentData?.users;
  const isPrivate = currentData?.private === true;
  const isLoading = tab === "followers" ? followersLoading : followingLoading;
  const emptyMessage = isPrivate
    ? `This user's ${tab} list is private`
    : tab === "followers"
      ? "No followers yet"
      : "Not following anyone yet";

  return (
    <div className="fixed inset-0 bg-(--scroll-bg) z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-(--scroll-border) flex items-center justify-between pt-[max(1rem,env(safe-area-inset-top))]">
        <h2 className="text-(--scroll-text) text-lg font-semibold scroll-heading">
          @{username}
        </h2>
        <button onClick={onClose}>
          <X className="text-(--scroll-text)" size={18} strokeWidth={1.5} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-(--scroll-border)">
        <button
          onClick={() => setTab("followers")}
          className={`flex-1 py-3 text-[14px] text-center transition-colors ${
            tab === "followers"
              ? "font-semibold text-(--scroll-text) border-b-[1.5px] border-(--scroll-text)"
              : "font-medium text-(--scroll-text-tertiary) hover:text-(--scroll-text)"
          }`}
        >
          Followers
        </button>
        <button
          onClick={() => setTab("following")}
          className={`flex-1 py-3 text-[14px] text-center transition-colors ${
            tab === "following"
              ? "font-semibold text-(--scroll-text) border-b-[1.5px] border-(--scroll-text)"
              : "font-medium text-(--scroll-text-tertiary) hover:text-(--scroll-text)"
          }`}
        >
          Following
        </button>
      </div>

      {/* User list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-(--scroll-border) border-t-(--scroll-text) rounded-full animate-spin" />
          </div>
        ) : users && users.length > 0 ? (
          <div>
            {users.map((user) => (
              <button
                key={user.id}
                onClick={() => {
                  onClose();
                  navigate(`/${user.username}`);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-(--scroll-bg-secondary) transition-colors text-left"
              >
                {user.profileImageUrl ? (
                  <img
                    src={user.profileImageUrl}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-(--scroll-bg-tertiary) flex items-center justify-center flex-shrink-0">
                    <span className="text-(--scroll-text-tertiary) text-sm font-medium">
                      {(user.displayName || user.username || "?")
                        .charAt(0)
                        .toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-(--scroll-text) text-sm font-medium truncate flex items-center gap-1">
                    {user.displayName || user.username}
                    <CreatorBadge verified={!!user.isVerified} accentColor={user.accentColor ?? null} size="sm" />
                  </p>
                  <p className="text-(--scroll-text-tertiary) text-xs truncate">
                    @{user.username}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-(--scroll-text-tertiary)">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
}
